import type { ReactNode } from 'react';
import { Section } from '@elfsight/lib-stripe-integration';
import { createReadableClass } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { CLASS_COMPONENT, TEXT_ACTION_ELEMENT_FONT_STYLE } from '../../common';
import { ActionLink } from './action-link';
import { Text } from './text';
import { Title } from './title';

type Dividers = {
  top?: boolean;
  bottom?: boolean;
};

export type PaymentInfoProps = {
  color: string;
  accentColor: string;
  cardNumber: string | null;
  displayDate: string;
  displayPrice: string;
  displayPaymentMethodType: string | null;
  receiptUrl: string;
  dividers?: Dividers;
};

export function PaymentInfoBlock({
  color,
  accentColor,
  displayDate,
  displayPrice,
  displayPaymentMethodType,
  cardNumber,
  receiptUrl,
  dividers = {
    top: false,
    bottom: false
  }
}: PaymentInfoProps) {
  const { t } = useLingui();

  const paidValue = getPaidValue({
    displayPrice,
    displayPaymentMethodType,
    cardNumber
  });

  return (
    <Section
      $align="flex-start"
      $color={color}
      $gap={4}
      $withTopDivider={dividers.top}
      $withBottomDivider={dividers.bottom}
      $classModifier="payment-info"
    >
      <Title>{t`Payment Details`}</Title>

      <Section
        $align="flex-start"
        $classModifier="payment-info-data"
        $gap={0}
        $withoutTopPadding
        $withoutBottomPadding
      >
        <PaymentInfoDataRecord
          label={t`Paid`}
          value={paidValue}
          classModifier="paid"
        />
        <PaymentInfoDataRecord
          label={t`Paid on`}
          value={displayDate}
          classModifier="paid-on"
        />
      </Section>

      <ActionLink $color={accentColor} href={receiptUrl} target="_blank">
        <Text textStyle={TEXT_ACTION_ELEMENT_FONT_STYLE}>
          {t`View Receipt`}
        </Text>
      </ActionLink>
    </Section>
  );
}

function PaymentInfoDataRecord({
  label,
  value,
  classModifier
}: {
  label: ReactNode;
  value: ReactNode;
  classModifier?: string;
}) {
  return (
    <Text
      className={createReadableClass({
        component: CLASS_COMPONENT,
        element: 'payment-info-data-record',
        modifier: classModifier
      })}
      textStyle={{ textAlign: 'left' }}
    >
      <span
        className={createReadableClass({
          component: CLASS_COMPONENT,
          element: 'payment-info-data-record',
          modifier: 'label'
        })}
      >
        {label}
      </span>
      :{' '}
      <span
        className={createReadableClass({
          component: CLASS_COMPONENT,
          element: 'payment-info-data-record',
          modifier: 'value'
        })}
      >
        {value}
      </span>
    </Text>
  );
}

function getPaidValue({
  displayPrice,
  displayPaymentMethodType,
  cardNumber
}: {
  displayPrice: string;
  displayPaymentMethodType: string | null;
  cardNumber: string | null;
}) {
  if (!displayPaymentMethodType) {
    return displayPrice;
  }

  if (!cardNumber) {
    return `${displayPrice} (${displayPaymentMethodType})`;
  }

  return `${displayPrice} (${displayPaymentMethodType} •••• ${cardNumber})`;
}
